/**
 * Thin GA4 wrapper used by all event call sites.
 *
 * - Initialization happens once in src/index.js. This module never re-initializes.
 * - Every call is wrapped in try/catch — analytics failures must never break the
 *   user-facing flow (signup, study session, etc.). Worst case we log and move on.
 * - All event names are lowercase_snake_case to follow GA4 conventions.
 */
import ReactGA from 'react-ga4';
import { getCampaignAttributionParams } from './campaignAttribution';
import { getLandingIntentAttributionParams } from './landingIntent';

/**
 * Fire a GA4 event. No-op if GA isn't initialized yet (e.g. local dev where
 * the init was skipped, blocked by adblocker, or in unit tests).
 *
 * @param {string} name - Event name (e.g. 'sign_up', 'first_rep_completed').
 * @param {Record<string, unknown>} [params] - Event parameters.
 */
export const track = (name, params = {}) => {
  try {
    if (!ReactGA.isInitialized) return;
    ReactGA.event(name, { ...getCampaignAttributionParams(), ...params });
  } catch (err) {
    console.warn(`[analytics] failed to send ${name}:`, err);
  }
};

/**
 * GA4 recommended event: sign_up. `method` is the standard parameter for
 * distinguishing email vs social signups in the Acquisition reports.
 *
 * @param {'email' | 'google' | string} method
 */
export const trackSignUp = (method, extraParams = {}) => {
  track('sign_up', {
    method,
    ...getLandingIntentAttributionParams(),
    ...extraParams,
  });
};

/**
 * Activation event: user finished their very first flashcard rep. Fires
 * exactly once per user — call sites are responsible for the one-shot guard
 * (typically a userdoc field like `firstRepCompletedAt`).
 *
 * Pair with `attempt_count` (how many submit clicks before the rep landed) to
 * read demo friction from GA4: a long tail of high attempt counts on the
 * onboarding snippet is a signal that the typing tolerance is too tight or
 * the prompt is unclear, not that the funnel is broken downstream.
 *
 * @param {{
 *   deck_id?: string,
 *   card_id?: string,
 *   language?: string,
 *   attempt_count?: number
 * }} [params]
 */
export const trackFirstRepCompleted = (params = {}) => {
  track('first_rep_completed', params);
};

/**
 * Retention event: app load for an authenticated, non-guest user. Fires at
 * most once per local calendar day (call sites guard via localStorage so the
 * SPA's frequent re-mounts don't inflate DAU).
 */
export const trackDailySession = () => {
  track('daily_session', {});
};

/**
 * Retention event: user reached a day-streak milestone (3 / 7 / 30). Call
 * sites must dedupe per milestone so we don't fire the same value twice.
 *
 * @param {3 | 7 | 30 | number} milestone
 */
export const trackStreakMilestone = (milestone) => {
  track('streak_milestone', {
    milestone_days: milestone,
    value: milestone,
  });
};

/**
 * Guest demo flow events. Five beats means four drop-off points; these
 * helpers produce the funnel readout described in the demo plan:
 *
 *   demo_started (`learn-catalog` / `track-study`) ->
 *   rep1_completed -> session_completed -> signup
 *
 * gate_shown / gate_dismissed give the explicit conversion rate inside the
 * final drop-off.
 */
export const trackDemoStarted = (params = {}) => {
  track('demo_started', params);
};

export const trackLanguagePicked = (language) => {
  track('language_picked', { language });
};

export const trackRep1Completed = (params = {}) => {
  track('rep1_completed', params);
};

export const trackSessionCompleted = (params = {}) => {
  track('session_completed', params);
};

export const trackGateShown = (params = {}) => {
  track('gate_shown', params);
};

export const trackGateDismissed = (reason) => {
  track('gate_dismissed', { reason: reason || 'unknown' });
};

/**
 * Funnel-close event: the signup prompt actually rendered to the user. Fires
 * alongside `gate_shown`; kept as a separate event so the demo dashboard can
 * use the clearer `first_rep_completed → signup_prompt_shown → signup_completed`
 * naming without rebuilding off internal "gate" jargon.
 *
 * @param {{ surface?: string }} [params] - `surface` identifies which UI
 *   showed the prompt (e.g. 'guest-save-modal', 'soft-gate-banner') so we can
 *   compare conversion by entry point.
 */
export const trackSignupPromptShown = (params = {}) => {
  track('signup_prompt_shown', params);
};

/**
 * Funnel-close event: the user converted (anon → real account, or fresh
 * signup). Fires alongside the GA4-recommended `sign_up` event — `sign_up`
 * stays for the built-in Acquisition reports, `signup_completed` is the
 * matching bookend to `signup_prompt_shown` in the demo funnel readout.
 *
 * @param {{ method: 'email' | 'google' | string, surface?: string }} params
 *   - `method` matches the `sign_up` event's method parameter.
 *   - `surface` (optional) — where the conversion fired from
 *     (e.g. 'guest-save-modal', 'auth-page').
 */
export const trackSignupCompleted = (params = {}) => {
  track('signup_completed', {
    ...getLandingIntentAttributionParams(),
    ...params,
  });
};

/**
 * Role onboarding: user picked a primary role (post-signup picker).
 * @param {{ role_id: string, surface?: string }} params
 */
export const trackRoleSelected = (params = {}) => {
  track('role_selected', params);
};

/**
 * Role switch from Profile (or elsewhere).
 * @param {{ role_id: string, previous_role_id?: string|null, surface?: string }} params
 */
export const trackRoleSwitched = (params = {}) => {
  track('role_switched', params);
};

/**
 * Role leveled up after study EXP.
 * @param {{ role_id: string, level: number, track_id?: string|null }} params
 */
export const trackRoleLevelUp = (params = {}) => {
  track('role_level_up', params);
};

/**
 * User opened a track from a role path surface (Home / Profile / Learn).
 * @param {{ role_id: string, track_id: string, surface?: string }} params
 */
export const trackRolePathTrackStarted = (params = {}) => {
  track('role_path_track_started', params);
};

/**
 * SRS grading: inferred FSRS grade from a Check submission (regular study mode).
 * Use GA4 to tune RATING_PASS_ACCURACY / RATING_GOOD_ACCURACY over time.
 *
 * @param {{
 *   rating: string,
 *   accuracy?: number,
 *   total_lines?: number,
 *   wrong_line_count?: number,
 *   prior_failed_checks?: boolean,
 *   study_mode?: string,
 *   language?: string,
 *   deck_id?: string,
 *   card_id?: string
 * }} params
 */
export const trackSrsGradeInferred = (params = {}) => {
  track('srs_grade_inferred', params);
};

export default {
  track,
  trackSignUp,
  trackFirstRepCompleted,
  trackDailySession,
  trackStreakMilestone,
  trackDemoStarted,
  trackLanguagePicked,
  trackRep1Completed,
  trackSessionCompleted,
  trackGateShown,
  trackGateDismissed,
  trackSignupPromptShown,
  trackSignupCompleted,
  trackRoleSelected,
  trackRoleSwitched,
  trackRoleLevelUp,
  trackRolePathTrackStarted,
  trackSrsGradeInferred,
};
